import { z } from "zod";
import { LocalDate, localDateAt } from "./schedule.ts";
import { UserId } from "./user.ts";
import { SUBJECT_TITLES, Subject } from "./subjects.ts";

/**
 * Анкета ученика и учёт оплат.
 *
 * **Почему всё это лежит на ПАРЕ, а не на человеке.** Модель проекта это многие
 * ко многим: один ученик занимается у нескольких преподавателей (раздел 5.10
 * CLAUDE.md). Тариф, режим оплаты, телефон мамы и заметка «тянет с оплатой» это
 * то, что собрал и записал КОНКРЕТНЫЙ преподаватель. Положив их на `app_user`,
 * мы бы отдали контакты матери ребёнка второму преподавателю, который её
 * никогда не видел, а согласия он не собирал.
 *
 * ⚠️ **Ни одно поле этого файла не уезжает ученику ни одной ручкой.** Это не
 * стиль, а то же правило, что у `schedule_series.note` (раздел 9
 * `docs/schedule-design.md`): в такие поля пишут то, чего вторая сторона видеть
 * не должна, и «тянет с оплатой» там названо буквально.
 *
 * ⚠️ **Контакт родителя это НЕ родительский аккаунт.** `app_user.guardian_user_id`
 * это человек со входом, будущий родительский кабинет (раздел 1 CLAUDE.md).
 * Здесь же строка в записной книжке преподавателя. Путать их нельзя: иначе с
 * появлением родительского экрана у ученика окажется два родителя, и разойдутся
 * они в первый же месяц.
 *
 * ---
 *
 * **Два уровня учёта, и они отвечают на разные вопросы** (решение владельца
 * 21.08.2026).
 *
 * `TeacherTracking` это ОДНА ручка на всех учеников: «я вообще веду учёт
 * посещений» и «я вообще веду учёт оплат». Живёт на человеке, потому что это
 * свойство рабочей привычки, а не отношений с конкретным ребёнком. Выключено по
 * умолчанию: пока переключатель не тронут, ни карточка ученика, ни сетка
 * расписания не показывают об учёте ничего.
 *
 * `PaymentMode` это КАК платят за конкретную пару. Здесь различие настоящее:
 * один платит после каждого занятия, другой переводит раз в месяц, третий
 * родственник и не платит вовсе.
 */

/* ───────────────────────────── контакты ────────────────────────────── */

/**
 * Чем связываются. Не транспорт, а справочник.
 *
 * ⚠️ Telegram здесь законен, и это не противоречит запрету раздела 4 CLAUDE.md.
 * Там запрещён Telegram **как транспорт критичного уведомления**, то есть наша
 * отправка через него. Здесь же записано, куда пишет сам преподаватель со
 * своего телефона. Кнопки «отправить» у этих полей нет и не будет, пока у
 * продукта нет разрешённого транспорта.
 *
 * `phone` в списке означает «звонить голосом»: телефонный номер бывает у
 * человека, которому звонят, но не пишут, и наоборот.
 */
export const ContactChannel = z.enum([
  "phone",
  "sms",
  "telegram",
  "whatsapp",
  "vk",
  "email",
  "other",
]);
export type ContactChannel = z.infer<typeof ContactChannel>;

export const CONTACT_CHANNEL_TITLES: Record<ContactChannel, string> = {
  phone: "звонок",
  sms: "СМС",
  telegram: "Telegram",
  whatsapp: "WhatsApp",
  vk: "ВКонтакте",
  email: "почта",
  other: "другое",
};

export const CONTACTS_PER_STUDENT_MAX = 6;
export const CONTACT_NAME_MAX = 80;
export const CONTACT_PHONE_MAX = 32;
export const CONTACT_LINK_MAX = 2000;
export const CONTACT_NOTE_MAX = 300;

/**
 * Телефон хранится КАК НАПИСАН, а не в E.164.
 *
 * Преподаватель пишет «+7 916 123-45-67», и переписывать это за него значит
 * получить поле, где человек не узнаёт номер, который сам ввёл. Нормализация
 * нужна ровно одному потребителю, ссылке `tel:`, и делает её `telHref` в момент
 * отрисовки.
 *
 * Форма проверяется слабо намеренно: белый список форматов ломается на первом
 * же добавочном, на городском номере без кода и на бабушке из Казахстана.
 * Требуем лишь, чтобы в строке было хотя бы пять цифр и не было ничего, кроме
 * цифр, плюса, пробелов, скобок и дефисов.
 */
export const PHONE_SHAPE = /^[+()\d\s-]{5,}$/;

export const ContactPhone = z
  .string()
  .max(CONTACT_PHONE_MAX)
  .regex(PHONE_SHAPE, "телефон: цифры, плюс, скобки, пробелы и дефисы")
  .refine((v) => (v.match(/\d/g)?.length ?? 0) >= 5, "в телефоне меньше пяти цифр");

/**
 * Ссылка на профиль (ВКонтакте, канал, что угодно).
 *
 * ⚠️ Правило то же и по той же причине, что у `external_call_url` (раздел 5
 * миграции `1788393600000_external_call_url`): только `https://` и ни одного
 * пробельного символа. Это чужой адрес, который рисуется как `href`, а
 * `javascript:` и `data:` в таком поле это готовый XSS.
 *
 * Ученику он при этом не показывается никогда, в отличие от ссылки на созвон,
 * но правило одно: поле, которое однажды станет кликабельным, обязано быть
 * безопасным с первого дня, а не с того, когда его начнут показывать.
 */
export const CONTACT_LINK_SHAPE = /^https:\/\/\S{3,}$/;

export const ContactLink = z
  .string()
  .max(CONTACT_LINK_MAX)
  .regex(CONTACT_LINK_SHAPE, "ссылка должна начинаться с https:// и быть без пробелов");

/**
 * Строка записной книжки: один человек.
 *
 * Человек, а не канал: у мамы бывает и телефон, и телеграм, и это один
 * собеседник, а не два. Иначе список из четырёх строк описывал бы двух людей, и
 * «кому звонить» перестало бы читаться взглядом.
 */
export const StudentContact = z.object({
  id: z.string().uuid(),
  /** «Ирина, мама». Роль отдельным полем не заводим: она часть того, как зовут. */
  name: z.string().min(1).max(CONTACT_NAME_MAX),
  phone: ContactPhone.nullable(),
  link: ContactLink.nullable(),
  /** Пустой список законен: у контакта может быть только телефон и ничего больше. */
  channels: z.array(ContactChannel).max(ContactChannel.options.length),
  note: z.string().max(CONTACT_NOTE_MAX).nullable(),
});
export type StudentContact = z.infer<typeof StudentContact>;

/**
 * Заведение и правка контакта.
 *
 * ⚠️ Пустая строка на входе означает «стереть», и приводит её к `null` сервер, а
 * не форма: две нормализации разъезжаются, и тогда контакт с пробелом в поле
 * телефона проходит форму и падает на `CHECK` в базе.
 */
export const ContactInput = z.object({
  name: z.string().min(1).max(CONTACT_NAME_MAX),
  phone: ContactPhone.nullable().optional(),
  link: ContactLink.nullable().optional(),
  channels: z.array(ContactChannel).max(ContactChannel.options.length).optional(),
  note: z.string().max(CONTACT_NOTE_MAX).nullable().optional(),
});
export type ContactInput = z.infer<typeof ContactInput>;

/**
 * Номер для `tel:`.
 *
 * Отдельная функция, а не выражение в разметке, потому что у неё есть отказ:
 * строка, из которой не собирается набираемый номер, обязана давать не битую
 * ссылку, а обычный текст. Ведущий плюс сохраняется, всё остальное кроме цифр
 * выбрасывается.
 */
export function telHref(phone: string | null): string | null {
  if (phone === null) return null;
  const plus = phone.trimStart().startsWith("+");
  const digits = phone.replace(/\D/g, "");
  if (digits.length < 5) return null;
  return `tel:${plus ? "+" : ""}${digits}`;
}

/* ────────────────────── учёт: переключатели и режим ─────────────────── */

/**
 * Ведёт ли преподаватель учёт. Одна ручка на всех учеников.
 *
 * ⚠️ Оба поля выключены по умолчанию, и это и есть механизм «не назойливо»:
 * пока человек их не включил, ни одна плитка, ни одна колонка сетки и ни одна
 * панель об учёте не появляется. Молчащего раздела, который есть и пуст, нет:
 * пустое место на экране это тоже сообщение, и читается оно как «сюда надо
 * что-то внести».
 *
 * Два поля, а не одно: посещаемость считает тот, кто ведёт абонементы и
 * пропуски, оплаты тот, кто следит за деньгами, и это разные люди чаще, чем
 * один и тот же.
 */
export const TeacherTracking = z.object({
  attendance: z.boolean(),
  payments: z.boolean(),
});
export type TeacherTracking = z.infer<typeof TeacherTracking>;

/**
 * Как платят за эту пару.
 *
 * ⚠️ `none` это «у этого ученика не веду» (родственник, обмен, бесплатно), а не
 * «бесплатно для продукта»: он гасит счёт по одной паре, не трогая остальные.
 * Целиком учёт выключается переключателем человека, см. `TeacherTracking`.
 *
 * `monthly` счётчика долга по занятиям НЕ даёт, и это названо честно: там
 * единица оплаты месяц, а не занятие. Вместо долга такая пара показывает число
 * проведённых занятий текущего месяца, то есть ровно ту величину, из которой
 * преподаватель считает сумму.
 */
export const PaymentMode = z.enum(["none", "per_lesson", "monthly"]);
export type PaymentMode = z.infer<typeof PaymentMode>;

export const PAYMENT_MODE_TITLES: Record<PaymentMode, string> = {
  none: "не веду",
  per_lesson: "за каждое занятие",
  monthly: "раз в месяц",
};

/**
 * Был ли ученик на занятии.
 *
 * Три состояния, а не два: отсутствие отметки это НЕ «не пришёл». Пропуск это
 * тот самый факт, ради которого учёт посещений и ведут, и заявить его молчанием
 * значит выставить прогульщиком каждого, до кого не дошли руки.
 */
export const Attendance = z.enum(["present", "absent"]);
export type Attendance = z.infer<typeof Attendance>;

export const ATTENDANCE_TITLES: Record<Attendance, string> = {
  present: "был",
  absent: "не пришёл",
};

/* ───────────────────────── предметный слой ─────────────────────────── */

/**
 * Класс школьной программы.
 *
 * ⚠️ Ось разметки предметного слоя это класс и кодификатор ФИПИ, а не уровень
 * CEFR (раздел 1 CLAUDE.md, следствие 1 смены ЦА 20.08.2026). Поэтому поле не
 * декоративное: по нему будет подбираться материал, и свободным текстом оно
 * быть не может.
 *
 * `null` означает «не спрашивали», а не «нет класса»: у выпускника на пересдаче
 * класса уже нет.
 */
export const StudentGrade = z.number().int().min(1).max(11);
export type StudentGrade = z.infer<typeof StudentGrade>;

/**
 * Предмет пары. Закрытый список: предметы продукта (`Subject` из
 * `./subjects.ts`) плюс «другое». Собран из того списка, а не выписан заново,
 * чтобы новый предмет не пришлось помнить в двух местах.
 *
 * `other` есть, потому что репетитор берёт и обществознание, и разрешать ему
 * соврать в анкете хуже, чем принять честное «другое».
 */
export const StudentSubject = z.enum([...Subject.options, "other"]);
export type StudentSubject = z.infer<typeof StudentSubject>;

/**
 * Подписи предмета пары в анкете, строчными, как соседние поля анкеты.
 *
 * ⚠️ Имя `STUDENT_SUBJECT_TITLES`, а не `SUBJECT_TITLES`: второе с появлением
 * `./subjects.ts` занято подписями предметов преподавателя, и два одинаковых
 * имени в корне `@edu/shared` не собрались бы. Сами слова берутся оттуда же.
 */
export const STUDENT_SUBJECT_TITLES: Record<StudentSubject, string> = {
  math: SUBJECT_TITLES.math.toLowerCase(),
  english: SUBJECT_TITLES.english.toLowerCase(),
  russian: SUBJECT_TITLES.russian.toLowerCase(),
  german: SUBJECT_TITLES.german.toLowerCase(),
  other: "другое",
};

/** Зачем занимаются. Та же ось ФИПИ, что у класса. */
export const StudentGoal = z.enum(["school", "oge", "ege", "other"]);
export type StudentGoal = z.infer<typeof StudentGoal>;

export const GOAL_TITLES: Record<StudentGoal, string> = {
  school: "школьная программа",
  oge: "ОГЭ",
  ege: "ЕГЭ",
  other: "другое",
};

export const STUDENT_NOTE_MAX = 2000;

/* ──────────────────────── ключ занятия ─────────────────────────────── */

/**
 * На чём держится отметка оплаты.
 *
 * Две формы, и это не небрежность, а форма самой модели. У занятия серии нет
 * строки в базе, пока оно не стало уроком (решение владельца 3,
 * `docs/schedule-design.md`, раздел 1), поэтому ключом ему служит пара
 * `(slot_id, occurs_on)`: она стабильна, переживает перенос и правку слота, и
 * названа правильным ключом для учёта оплат ещё в разделе 15 того же документа.
 * У разового и у ручного урока слота нет вовсе, и ключ у него только один, свой
 * собственный `lesson.id`.
 *
 * ⚠️ **Правило выбора формы одно и оно не про то, есть ли урок.** Занятие серии
 * адресуется парой ВСЕГДА, даже когда урок уже создан. Иначе одно и то же
 * занятие получило бы два адреса (до материализации и после) и было бы отмечено
 * дважды, а счётчик долга разошёлся бы с тем, что видит глаз. Разрешение живёт
 * в одном месте, `occurrenceKeyOfLesson`.
 */
export const OccurrenceKey = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("occurrence"),
    slotId: z.string().uuid(),
    occursOn: LocalDate,
  }),
  z.object({
    kind: z.literal("lesson"),
    lessonId: z.string().uuid(),
  }),
]);
export type OccurrenceKey = z.infer<typeof OccurrenceKey>;

/**
 * Ключ занятия по уроку. Единственное место, где выбирается форма.
 *
 * Принимает ровно то, что есть у любого представления урока, чтобы её могли
 * звать и сервер, и браузер, не сговариваясь о типе.
 */
export function occurrenceKeyOfLesson(lesson: {
  id: string;
  slotId: string | null;
  occursOn: string | null;
}): OccurrenceKey {
  return lesson.slotId !== null && lesson.occursOn !== null
    ? { kind: "occurrence", slotId: lesson.slotId, occursOn: lesson.occursOn }
    : { kind: "lesson", lessonId: lesson.id };
}

/** Строкой, чтобы класть в `Map` и в `key` списка. Форма входит в строку. */
export function occurrenceKeyString(key: OccurrenceKey): string {
  return key.kind === "occurrence" ? `o:${key.slotId}:${key.occursOn}` : `l:${key.lessonId}`;
}

/**
 * Форма `occurrenceKeyString` в адресе (`?occurrence=` у слов занятия, 9.3
 * `docs/flashcards-design.md`). Только нижний регистр: строку собирает
 * `occurrenceKeyString` из uuid Postgres, а ключ это ещё и колонка привязки, и
 * `O:…` с заглавными дал бы второй адрес того же занятия.
 */
export const OCCURRENCE_KEY_STRING_RE =
  /^(?:o:([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}):(\d{4}-\d{2}-\d{2})|l:([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}))$/;
export const OccurrenceKeyString = z.string().regex(OCCURRENCE_KEY_STRING_RE);

/** Обратная к `occurrenceKeyString`; `null` у мусора. Календарь даты не сверяется, как у `LocalDate`. */
export function parseOccurrenceKeyString(s: string): OccurrenceKey | null {
  const m = OCCURRENCE_KEY_STRING_RE.exec(s);
  if (m === null) return null;
  const key: OccurrenceKey =
    m[3] !== undefined
      ? { kind: "lesson", lessonId: m[3] }
      : { kind: "occurrence", slotId: m[1] as string, occursOn: m[2] as string };
  return OccurrenceKey.safeParse(key).success ? key : null;
}

/* ──────────────────────── занятия, долг, отметки ───────────────────── */

/**
 * Прошедшее занятие пары в панели учёта.
 *
 * Занятие, а не урок. У преподавателя, который ведёт в Zoom и в нашу комнату не
 * заходит (режим сосуществования, раздел 2 CLAUDE.md, штатная конфигурация
 * продукта), уроков не создаётся вовсе, и список из уроков был бы у него пуст
 * навсегда. Поэтому список собирается раскрытием расписания, а уроки без слота
 * доливаются к нему отдельно. Отсюда и строки без названия: занятие было, урока
 * в приложении не было.
 */
export const MarkedOccurrence = z.object({
  key: OccurrenceKey,
  at: z.number().int(),
  /** Название урока, если занятие им стало. `null` это «урока не было». */
  title: z.string().nullable(),
  /** Когда преподаватель отметил оплату. `null` это «не отмечено». */
  paidAt: z.number().int().nullable(),
  /** `null` это «не отмечали», а НЕ «не пришёл». */
  attendance: Attendance.nullable(),
});
export type MarkedOccurrence = z.infer<typeof MarkedOccurrence>;

/* ─────────────────────────── месяцы оплаты ─────────────────────────── */

/**
 * Помесячная оплата: месяц это САМОСТОЯТЕЛЬНАЯ единица, а не группа занятий.
 *
 * ⚠️ **Это разворот первой редакции**, и разворот осознанный. Сначала месяц
 * считался просто способом посмотреть на те же поурочные отметки, и при
 * постоплате так и было бы. Ответ преподавателей 21.08.2026 («платят только
 * вперёд») это сломал: преподаватель отмечает сентябрь 25 августа, когда
 * сентябрьского расписания ещё нет целиком, потом заводит четверговый слот, и
 * месяц молча становится «оплачен частично», а счётчик показывает долг,
 * которого нет. Это ровно тот отказ, который владелец уже отверг: счётчик не
 * имеет права врать в сторону тревоги.
 *
 * Поэтому «сентябрь оплачен» это факт О МЕСЯЦЕ, не зависящий от того, сколько
 * занятий в нём в итоге окажется. Поурочные отметки (`occurrence_mark`)
 * остаются нетронутыми и обслуживают пары с `payment_mode = 'per_lesson'`.
 *
 * Следствие, за которое стоит держаться: смена режима ничего не теряет. Это
 * разные таблицы про разные периоды истории, а не две трактовки одних данных.
 */

/** Месяц в формате `ГГГГ-ММ`. Тот же порядок, что у `LocalDate`: сравним строкой. */
export const PERIOD_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

export const PaymentPeriod = z.string().regex(PERIOD_RE, "месяц в формате ГГГГ-ММ");
export type PaymentPeriod = z.infer<typeof PaymentPeriod>;

/**
 * Названия месяцев словарём, а не через `Intl`.
 *
 * Тот же приём и по той же причине, что у `WEEKDAYS` в расписании и у городов в
 * списке часовых поясов: у ICU для русского месяца два начертания
 * (именительное и родительное), и какое из них отдаст `month: 'long'`, зависит
 * от того, стоит ли рядом день. Нам нужно именительное всегда: чип подписан
 * месяцем, а не датой.
 *
 * Индекс это номер месяца минус один.
 */
export const MONTH_TITLES = [
  "январь", "февраль", "март", "апрель", "май", "июнь",
  "июль", "август", "сентябрь", "октябрь", "ноябрь", "декабрь",
] as const;

/** Короткое имя для чипа: «сен», «окт». Три буквы у всех, поэтому чипы ровные. */
export const MONTH_TITLES_SHORT = [
  "янв", "фев", "мар", "апр", "май", "июн",
  "июл", "авг", "сен", "окт", "ноя", "дек",
] as const;

/** Номер месяца периода, 1-12. Отдельно, потому что срез строки легко перепутать. */
function monthNo(period: string): number {
  return Number(period.slice(5, 7));
}

export function periodTitle(period: string): string {
  return MONTH_TITLES[monthNo(period) - 1] ?? period;
}

export function periodTitleShort(period: string): string {
  return MONTH_TITLES_SHORT[monthNo(period) - 1] ?? period;
}

/**
 * Месяц момента в ЛОКАЛЬНОЙ зоне.
 *
 * ⚠️ Через `localDateAt`, а не через `new Date(at).getMonth()`. Занятие 31
 * августа в 23:30 по Москве это 20:30 UTC того же дня, а занятие 1 сентября в
 * 00:30 по Москве это 21:30 UTC ТРИДЦАТЬ ПЕРВОГО августа. Наивный расчёт по UTC
 * положил бы второе в август, то есть переставил бы деньги из месяца в месяц.
 */
export function periodOf(at: number, zone: string): string {
  return localDateAt(at, zone).slice(0, 7);
}

/**
 * Сдвиг месяца на `months` вперёд или назад.
 *
 * Арифметикой по номеру месяца от нашей эры, а не через `Date`: `setMonth` на
 * 31 января плюс один месяц даёт 3 марта, и это ровно та ловушка, из-за которой
 * такие функции пишут руками.
 */
export function shiftPeriod(period: string, months: number): string {
  const total = Number(period.slice(0, 4)) * 12 + (monthNo(period) - 1) + months;
  const year = Math.floor(total / 12);
  // Двойной остаток: у отрицательного `%` в JS отрицательный результат
  const month = (((total % 12) + 12) % 12) + 1;
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}`;
}

export const MonthPayment = z.object({
  period: PaymentPeriod,
  /** Занятий в этом месяце по расписанию. Ноль законен: месяц мог быть пустым. */
  lessons: z.number().int(),
  /** Когда преподаватель отметил месяц оплаченным. `null` это «не отмечен». */
  paidAt: z.number().int().nullable(),
});
export type MonthPayment = z.infer<typeof MonthPayment>;

/**
 * Сколько месяцев показывать чипами: назад, текущий и вперёд.
 *
 * Вперёд ровно два, потому что предоплата это «за следующий месяц», а изредка
 * «за два вперёд» (решение владельца 21.08.2026). Назад три, чтобы был виден
 * недавний хвост, но не полгода: месяц старше квартала уже не выясняют.
 *
 * ⚠️ Цена отсечки названа честно: оплату на пять месяцев вперёд чипами не
 * поставить. Случай не назван ни разу, а лишние чипы стоят ширины строки.
 */
export const MONTHS_BACK = 3;
export const MONTHS_FORWARD = 2;

/**
 * Сколько дней вперёд раскрывается расписание ради месячных чипов.
 *
 * С запасом относительно `MONTHS_FORWARD`, и запас не лишний: «сейчас» бывает
 * первым числом месяца, и тогда до конца второго месяца вперёд остаётся почти
 * 92 дня. Ста двадцати хватает при любом положении «сейчас» внутри месяца.
 * Стоит этот запас только арифметики в памяти: раскрытие серии это не запрос.
 */
export const PAYMENT_FORWARD_DAYS = 120;

/**
 * Месяцы чипами: сколько в каждом занятий и отмечен ли он.
 *
 * Чистая функция, потому что группировка по локальному месяцу это то самое
 * место, где ошибаются на границе зоны, а проверить её можно только тестом.
 *
 * ⚠️ Считается В ПРИЛОЖЕНИИ, а не запросом с `AT TIME ZONE`. Это предупреждение
 * записано заранее в разделе 15 `docs/schedule-design.md`: имя зоны в SQL по
 * правилу 2.5 не поедет, поэтому месячная агрегация обязана собираться тем же
 * раскрытием, что и всё остальное.
 *
 * Порядок ХРОНОЛОГИЧЕСКИЙ, слева направо: месяцы читаются как лента времени, и
 * «новые сверху», уместное в списке занятий, здесь читалось бы задом наперёд.
 */
export function buildMonths(args: {
  occurrences: readonly { at: number }[];
  /** Отметки месяцев: период и момент отметки. */
  paidPeriods: ReadonlyMap<string, number>;
  zone: string;
  now: number;
}): MonthPayment[] {
  const current = periodOf(args.now, args.zone);

  const lessons = new Map<string, number>();
  for (const o of args.occurrences) {
    const p = periodOf(o.at, args.zone);
    lessons.set(p, (lessons.get(p) ?? 0) + 1);
  }

  const out: MonthPayment[] = [];
  for (let i = -MONTHS_BACK; i <= MONTHS_FORWARD; i += 1) {
    const period = shiftPeriod(current, i);
    out.push({
      period,
      lessons: lessons.get(period) ?? 0,
      paidAt: args.paidPeriods.get(period) ?? null,
    });
  }
  return out;
}

/**
 * Что сказать про помесячную оплату одной строкой.
 *
 * Правило целиком, и оно держится на предоплате:
 *
 * 1. **месяц становится должным, как только начался.** Не когда кончился:
 *    платят вперёд, и сентябрь ожидается оплаченным первого сентября, а не
 *    первого октября. Самый ранний такой неоплаченный это `unpaid`
 * 2. пустой месяц (занятий нет) в долг не идёт: платить не за что
 * 3. иначе говорим, ПО КАКОЙ месяц закрыто, и это последний месяц, до которого
 *    включительно каждый месяц либо оплачен, либо пуст
 *
 * ⚠️ Непрерывность в пункте 3 обязательна, и без неё фраза врала. Сценарий:
 * август отмечен, сентябрь нет, октябрь отмечен (заплатили за октябрьский
 * интенсив вперёд). Взяв просто самый поздний отмеченный, мы сказали бы
 * «оплачен по октябрь», умолчав про сентябрь. Цена ошибки выше обычной: в окне
 * карточки рисуется только сводка, и эта фраза там ЕДИНСТВЕННЫЙ источник, чипов
 * рядом нет.
 *
 * ⚠️ Пункт 3 сформулирован через «оплачен ИЛИ пуст», а не через «оплачен
 * подряд», и это не то же самое. Месяц без занятий (каникулы, пауза) цепочку не
 * рвёт: платить за него было не за что, и останавливаться на нём значило бы
 * назвать закрытым меньше, чем закрыто на самом деле.
 *
 * ⚠️ Слова «просрочен» здесь нет и не будет. Просрочка требует срока, а срока
 * мы не знаем: какого числа семья договорилась переводить деньги, продукту
 * неизвестно, и выдумать его значит выдумать долг у того, кто просто не
 * отмечает. `unpaid` это факт («сентябрь не отмечен»), а не обвинение.
 */
export type MonthlyState =
  | { kind: "unpaid"; period: string }
  | { kind: "paid_through"; period: string }
  | { kind: "empty" };

export function monthlyState(
  months: readonly MonthPayment[],
  currentPeriod: string,
): MonthlyState {
  /*
    Самый ранний ДОЛЖНЫЙ неоплаченный. Строки `ГГГГ-ММ` сравнимы
    лексикографически ровно так же, как хронологически, поэтому это обычное
    сравнение, а не разбор даты.
  */
  let unpaid: string | null = null;
  for (const m of months) {
    if (m.paidAt !== null || m.lessons === 0 || m.period > currentPeriod) continue;
    if (unpaid === null || m.period < unpaid) unpaid = m.period;
  }
  if (unpaid !== null) return { kind: "unpaid", period: unpaid };

  /*
    Проход хронологически от самого раннего чипа. Идём, пока месяц либо оплачен,
    либо пуст; первый неоплаченный месяц С ЗАНЯТИЯМИ останавливает. Ответ это
    последний ОПЛАЧЕННЫЙ месяц, до которого дошли: сказать «оплачен по» про
    пустой месяц нельзя, за него никто не платил.

    Сортируем свою копию: полагаться на порядок аргумента значит завести
    молчаливое требование, которое некому проверить.
  */
  const sorted = [...months].sort((a, b) => (a.period < b.period ? -1 : a.period > b.period ? 1 : 0));
  let paidThrough: string | null = null;
  for (const m of sorted) {
    if (m.paidAt !== null) {
      paidThrough = m.period;
      continue;
    }
    if (m.lessons === 0) continue;
    break;
  }

  return paidThrough === null ? { kind: "empty" } : { kind: "paid_through", period: paidThrough };
}

/**
 * Сколько дней назад смотрит панель учёта и счётчик долга.
 *
 * Полугодие, и это осознанная отсечка, а не техническая. Поурочный долг старше
 * полугода не взыскивают, а раскрытие бессрочных серий за все годы стоило бы
 * заметного времени на каждом открытии карточки.
 */
export const PAYMENT_WINDOW_DAYS = 180;

/**
 * Начало окна учёта оплат: самое раннее занятие, которое ещё идёт в счётчик
 * «не отмечено», в фильтр «Неоплаченные» и получает чип оплаты в списке.
 *
 * ⚠️ Одна формула на всех, и это не удобство. Счётчик считает сервер, чип
 * рисует браузер, и если граница у них разъедется хоть на сутки, в списке
 * появится строка с кнопкой «отметить», которую не видит ни счётчик, ни
 * фильтр: нажатие ничего не изменит в числе у заголовка.
 */
export function paymentWindowStart(now: number): number {
  return now - PAYMENT_WINDOW_DAYS * 86_400_000;
}

/** Сколько занятий показывает панель. Больше на экран всё равно не влезает. */
export const PAYMENT_OCCURRENCE_LIMIT = 120;

export const StudentPayment = z.object({
  mode: PaymentMode,
  /**
   * Занятий прошло и не отмечено оплаченными. Все, без точки отсчёта.
   *
   * ⚠️ Правило «считать с первой отметки» было отвергнуто владельцем
   * 21.08.2026: молча вычесть из долга всё, что было раньше, значит навредить
   * больше, чем показать честное большое число. Вместо этого есть быстрое
   * действие «отметить оплаченными всё до даты» (`SettlePaymentsRequest`):
   * тогда решение «до сюда закрыто» принимает человек, а не мы за него.
   *
   * ⚠️ `null` означает «у пары режим `none`» либо «учёт оплат выключен у
   * человека», то есть «этот вопрос здесь не задают». Ноль означает «всё
   * отмечено», и путать эти два ответа нельзя.
   */
  unpaidCount: z.number().int().nullable(),
  /**
   * Проведено занятий в текущем локальном месяце преподавателя.
   *
   * ⚠️ **Потребителей не осталось**: помесячная плитка перешла на `months` и
   * `monthlyState`. Поле оставлено на один релиз и удаляется следующей правкой.
   * Убрать его прямо сейчас нельзя по той же причине, по которой миграции идут
   * expand → contract: web едет отдельно от API, и вкладка со старым бандлом,
   * у которой поле объявлено обязательным, упала бы на разборе ответа без него.
   * Поэтому сначала `.optional()` здесь, потом удаление.
   */
  thisMonthCount: z.number().int().optional(),
  /** Отмечено пропусков за окно. `null`, когда учёт посещений выключен. */
  absentCount: z.number().int().nullable(),
  /** Прошедшие занятия окна, новые сверху. Пусто, когда учёт выключен весь. */
  occurrences: z.array(MarkedOccurrence),
  /**
   * Месяцы чипами, хронологически. Заполнено ТОЛЬКО у пары с режимом
   * `monthly`: у остальных единица оплаты не месяц, и пустой массив здесь
   * означает «этот вопрос тут не задают», а не «месяцев не нашлось».
   *
   * ⚠️ Долг помесячной пары живёт здесь, а НЕ в `unpaidCount`: там единица
   * занятие, здесь месяц, и складывать их нельзя. У помесячной пары
   * `unpaidCount` обязан оставаться `null`.
   */
  months: z.array(MonthPayment),
  /**
   * Какой месяц СЕЙЧАС, посчитанный сервером.
   *
   * ⚠️ Едет с сервера, а не считается клиентом, потому что считать его было
   * нечем. Сервер строит чипы в зоне СЕРИИ (там расписание и составлялось), а
   * клиент знает только зону смотрящего, и у пары без расписания зона серии это
   * запасной UTC. Разовый урок 1 сентября в 01:00 МСК ложился под чип «авг», а
   * подчёркнут был «сен»: два календаря на одном экране.
   *
   * Больнее всего это било по паре БЕЗ расписания, а это прямо названный
   * главный сценарий предоплаты: месяц отмечают, когда серии ещё нет.
   *
   * `null` означает «пары чипов нет» (режим не помесячный либо учёт выключен).
   */
  currentPeriod: PaymentPeriod.nullable(),
});
export type StudentPayment = z.infer<typeof StudentPayment>;

/**
 * Долг по списку занятий. Чистая функция, потому что правило решённое, а не
 * очевидное, и разъехаться двум его написаниям нельзя.
 *
 * Правило целиком: **считаем все прошедшие занятия окна, у которых нет отметки
 * оплаты.** Ни точки отсчёта, ни скидок на «недавно начал вести учёт»: число
 * обязано отвечать на вопрос «сколько занятий не закрыто», а не на вопрос
 * «сколько из них я согласен признать».
 *
 * ⚠️ Занятия ПОЗЖЕ `now` в долг не идут: занятие, которое ещё не состоялось, не
 * может быть не оплачено. При постоплате это очевидно, при предоплате счётчик
 * покажет на одно меньше, и это лучше, чем требовать денег вперёд от имени
 * преподавателя.
 *
 * ⚠️ Отмеченный пропуск (`absent`) из долга НЕ вычитается. Платят ли за
 * пропущенное, решают преподаватель с родителем, правил отмены у продукта нет
 * (раздел 9 docs/schedule-design.md), и вычесть молча значит решить этот вопрос
 * за них. Пропуск виден отдельным числом рядом.
 */
export function countUnpaid(
  occurrences: readonly { at: number; paidAt: number | null }[],
  now: number,
): number {
  let unpaid = 0;
  for (const o of occurrences) {
    if (o.paidAt !== null) continue;
    if (o.at > now) continue;
    unpaid += 1;
  }
  return unpaid;
}

/* ──────────────────────────── контракты ручек ──────────────────────── */

/**
 * Анкета пары одним объектом.
 *
 * Едет внутри `StudentDetail`, а не отдельной ручкой: карточка открывается
 * переходом из списка, и лишний круг по сети на канале 128 кбит/с (раздел 3.1
 * CLAUDE.md) человек видит как подвисание. Это то же соображение, по которому
 * `StudentDetail` уже собран из четырёх запросов в один ответ.
 */
export const StudentProfile = z.object({
  paymentMode: PaymentMode,
  grade: StudentGrade.nullable(),
  subject: StudentSubject.nullable(),
  goal: StudentGoal.nullable(),
  /** ⚠️ Приватная. Ученику не уезжает никогда, как `note` серии расписания. */
  note: z.string().nullable(),
  contacts: z.array(StudentContact),
});
export type StudentProfile = z.infer<typeof StudentProfile>;

/**
 * Правка анкеты. Каждое поле необязательно: экран правит их по одному, и
 * «поля нет» обязано означать «не трогай», а не «сотри».
 *
 * ⚠️ Тарифа здесь НЕТ, хотя поле тарифа в анкете нарисовано. Оно выключено с
 * подсказкой «работаем над этим»: колонка и контракт под то, что ничего не
 * пишет, это плата без покупки, и приедут они той же правкой, что и правило
 * пересчёта суммы.
 */
export const UpdateStudentProfileRequest = z.object({
  paymentMode: PaymentMode.optional(),
  grade: StudentGrade.nullable().optional(),
  subject: StudentSubject.nullable().optional(),
  goal: StudentGoal.nullable().optional(),
  note: z.string().max(STUDENT_NOTE_MAX).nullable().optional(),
});
export type UpdateStudentProfileRequest = z.infer<typeof UpdateStudentProfileRequest>;

/**
 * Отметка по одному занятию.
 *
 * Оба поля необязательны и означают «не трогай»: посещение и оплата это две
 * независимые кнопки в одной строке, и нажатие на одну не имеет права стирать
 * вторую. `null` у `attendance` означает «снять отметку посещения», `false` у
 * `paid` означает «снять отметку оплаты».
 */
export const SetMarkRequest = z.object({
  key: OccurrenceKey,
  paid: z.boolean().optional(),
  attendance: Attendance.nullable().optional(),
});
export type SetMarkRequest = z.infer<typeof SetMarkRequest>;

/**
 * «Отметить оплаченными все занятия до этой даты».
 *
 * Заведено вместо отвергнутой точки отсчёта (решение владельца 21.08.2026).
 * Разница принципиальная: точка отсчёта прятала старые занятия САМА, а это
 * действие человека, после которого в базе появляются настоящие отметки, и
 * каждую из них видно и можно снять.
 *
 * Дата ЛОКАЛЬНАЯ и граница СТРОГАЯ: «до 1 сентября» означает по 31 августа
 * включительно. Так же читается это словами, и так же оно написано на кнопке.
 */
export const SettlePaymentsRequest = z.object({
  before: LocalDate,
});
export type SettlePaymentsRequest = z.infer<typeof SettlePaymentsRequest>;

/** Сколько занятий закрылось этим действием. Ноль законен и о нём надо сказать. */
export const SettlePaymentsResponse = z.object({
  marked: z.number().int(),
});
export type SettlePaymentsResponse = z.infer<typeof SettlePaymentsResponse>;

/**
 * Отметить месяц оплаченным или снять отметку.
 *
 * `PUT` и состояние в теле по той же причине, что у `SetMarkRequest`: повтор
 * того же запроса обязан быть безвредным, потому что на нашей сети ответ
 * теряется чаще запроса (раздел 3.1 CLAUDE.md).
 *
 * ⚠️ Подтверждения у этого действия нет и не будет (решение владельца
 * 21.08.2026): нажатие ставит, повторное снимает. Действие обратимо одним
 * щелчком, а подтверждение платило бы щелчком за КАЖДОЕ правильное действие
 * ради редкого ошибочного. Тот же приём, что у чипов посещаемости.
 */
export const SetMonthPaymentRequest = z.object({
  period: PaymentPeriod,
  paid: z.boolean(),
});
export type SetMonthPaymentRequest = z.infer<typeof SetMonthPaymentRequest>;

export const StudentContactResponse = z.object({
  contact: StudentContact,
});
export type StudentContactResponse = z.infer<typeof StudentContactResponse>;

/** Переключатели учёта. Одна ручка на всех учеников. */
export const UpdateTrackingRequest = TeacherTracking.partial();
export type UpdateTrackingRequest = z.infer<typeof UpdateTrackingRequest>;

/** Идентификатор ученика в адресе. Здесь ради читаемости маршрутов. */
export const StudentUserId = UserId;
